import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import './App.css';
import GridInput from './pages/GridInput.js';
import Analysis from './pages/Analysis.js';
import Home from './pages/Home';
import Login from './pages/Login.js';
import TestPage from './pages/TestPage.js';
import TestPage2 from './pages/TestPage2.js';
import TestPage3 from './pages/TestPage3.js';
import TestPage4 from './pages/TestPage4.js';
import Total1 from './pages/Total1.js';
import Total2 from './pages/Total2.js';
import Total3 from './pages/Total3.js';
import FieldNote from './pages/FieldNote.js';
import { StickyNote, NotebookPen, ClipboardList } from 'lucide-react';

import api from './api/axiosConfig.js';

function App() {
  const [userName, setUserName] = useState(() => localStorage.getItem('userName') || '');
  const [userId, setUserId] = useState('');

  const [memoOpen, setMemoOpen] = useState(false);
  const [memoText, setMemoText] = useState(() => localStorage.getItem('memo') || '');

  // ✅ 모바일 메뉴 토글 상태 추가
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 모바일 메뉴 닫기 핸들러
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // 메모 저장
  const handleMemoSave = () => {
    api.post(`/api/Home/memoSave`, {userId, memoText})
      .then(() => { alert('저장되었습니다.'); })
      .catch(err => {
        if (err.response?.status === 409) alert(err.response.data);
        else alert('저장 중 오류가 발생했습니다.\n' + err);
      });
  };

  // 사용자정보 세팅
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
      api.get(`/api/login/userInfo?userId=${storedUserId}`)
        .then((res) => {
          const fetchedName = res.data?.name || res.data?.NAME || '';
          const fetchedMemo = res.data?.memo || res.data?.MEMO || '';
          setUserName(fetchedName);
          setMemoText(fetchedMemo);
          localStorage.setItem('userName', fetchedName);
          localStorage.setItem('memo', fetchedMemo);
        })
        .catch((err) => console.error(err));
    }
  }, []);

  // 메모 팝업이 열릴 때 재조회
  useEffect(() => {
    if (memoOpen && userId) {
      api.get(`/api/login/userInfo?userId=${userId}`)
        .then((res) => {
          const fetchedMemo = res.data?.memo || res.data?.MEMO || '';
          setMemoText(fetchedMemo);
        })
        .catch(err => console.error('메모 재조회 실패:', err));
    }
  }, [memoOpen, userId]);

  // 로그인 상태 관리
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return sessionStorage.getItem('isLoggedIn') === 'true';
  });

  // 로그인
  const handleLogin = (newUserId) => {
    setIsLoggedIn(true);
    sessionStorage.setItem('isLoggedIn', 'true');
    setUserId(newUserId);
    localStorage.setItem('userId', newUserId);

    api.get(`/api/login/userInfo?userId=${newUserId}`)
      .then((res) => {
        const fetchedMemo = res.data?.memo || res.data?.MEMO || '';
        const fetchedName = res.data.name || res.data.NAME || '';
        setUserName(fetchedName);
        setMemoText(fetchedMemo);
        localStorage.setItem('userName', fetchedName);
        localStorage.setItem('memo', fetchedMemo);
      })
      .catch(console.error);
  };

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('memo');
    localStorage.removeItem('userName');
    localStorage.removeItem('token');
    setUserName('');
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn("세션 만료 또는 토큰 오류로 자동 로그아웃 처리됩니다.");
      handleLogout();
    };
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [handleLogout]);

  const myPage = () => {
    console.log("마이페이지");
  };

  if (!isLoggedIn) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Login onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="app-wrapper">
        <nav className="navbar">

          {/* ✅ 1. 모바일 햄버거 버튼 추가 */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>

          {/* ✅ 2. 메뉴 클래스에 모바일 상태 연동 및 링크 클릭 시 닫기(closeMobileMenu) 적용 */}
          <ul className={`menu ${isMobileMenuOpen ? 'active' : ''}`}>
            <li><NavLink to="/home" onClick={closeMobileMenu}>홈</NavLink></li>
            <li><NavLink to="/mainData" onClick={closeMobileMenu}>매출매입 등록</NavLink></li>
            <li><NavLink to="/testPage" onClick={closeMobileMenu}>현장 등록</NavLink></li>
            <li><NavLink to="/testPage2" onClick={closeMobileMenu}>거래처 등록</NavLink></li>
            <li><NavLink to="/analysis" onClick={closeMobileMenu}>기간별 조회</NavLink></li>
            <li><NavLink to="/testPage4" onClick={closeMobileMenu}>현장별 조회</NavLink></li>
            <li><NavLink to="/total1" onClick={closeMobileMenu}>매출정산서</NavLink></li>
            <li><NavLink to="/total2" onClick={closeMobileMenu}>총괄정산서</NavLink></li>
            <li><NavLink to="/total3" onClick={closeMobileMenu}>수금현황</NavLink></li>
            <li><NavLink to="/fieldNote" onClick={closeMobileMenu}>현장 노트</NavLink></li>
          </ul>

          <div className="navbar-right">
            <div className="memo-wrapper">
              <button
                className={`memo-btn ${memoText ? 'has-memo' : ''}`}
                onClick={() => setMemoOpen(prev => !prev)}
                title="메모"
              >
                <StickyNote size={20} color="#2563EB" />
              </button>

              {memoOpen && (
                <div className="memo-dropdown">
                  <div className="memo-dropdown__header">
                    <span>메모</span>
                    <button className="memo-close" onClick={() => setMemoOpen(false)}>✕</button>
                  </div>
                  <textarea
                    className="memo-textarea"
                    value={memoText}
                    onChange={e => setMemoText(e.target.value)}
                    placeholder="메모를 입력하세요"
                  />
                  <div className="memo-dropdown__footer">
                    <button className="btn" onClick={handleMemoSave}>저장</button>
                    <button className="btn btn-ghost" onClick={() => setMemoOpen(false)}>닫기</button>
                  </div>
                </div>
              )}
            </div>

            <div className="user-profile" onClick={myPage}>
              <div className="user-avatar">
                {userName ? userName[0] : 'U'}
              </div>
              <span className="user-name">{userName}님</span>
            </div>

            <button className="logout-btn" onClick={handleLogout}>
              로그아웃
            </button>
          </div>
        </nav>

        {/* ✅ 3. 모바일에서 메뉴 열렸을 때 배경 흐리게 하는 오버레이 (선택 사항) */}
        {isMobileMenuOpen && (
          <div className="mobile-overlay" onClick={closeMobileMenu}></div>
        )}

        <main className="container">
          <div className="card">
            <Routes>
              <Route path="/home" element={<Home />} />
              <Route path="/mainData" element={<GridInput />} />
              <Route path="/analysis" element={<Analysis />} />
              <Route path="/testPage" element={<TestPage />} />
              <Route path="/testPage2" element={<TestPage2 />} />
              <Route path="/testPage3" element={<TestPage3 />} />
              <Route path="/testPage4" element={<TestPage4 />} />
              <Route path="/total1" element={<Total1 />} />
              <Route path="/total2" element={<Total2 />} />
              <Route path="/total3" element={<Total3 />} />
              <Route path="/fieldNote" element={<FieldNote />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </Router>
  );
}

export default App;